import { yupResolver } from '@hookform/resolvers/yup'
import { observer } from 'mobx-react-lite'
import { FunctionComponent, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  orderCallPopupStore,
  OrderCallType,
} from '@/store/OrderCallPopup.store'

import { getHelp } from '@/app/actions/help/actions'
import { marketingStorage } from '@/store/MarketingStorage'
import { HelpTypeSite } from '@/services/Api/generated'
import {
  acceptTermsValidator,
  phoneValidator,
  requiredFieldValidator,
} from '@/utils/validator'
import { object as yupObject } from 'yup'

import { AcceptCheckbox } from '@/components/AcceptCheckbox'
import { Button } from '@/components/Button'
import { FormInput } from '@/components/FormInput'
import { Loader } from '@/components/Loader'
import { Popup } from '@/components/Popup'

import { YANDEX_METRIKA, YANDEX_METRIKA_COUNTER } from '@/config'
import { useCaptcha } from '@/hooks/Captcha'

import { maxPopupStore, maxRequestFormTexts } from '@/store/MaxPopup.store'
import styles from './OrderCall.module.scss'

const MetricGoal: Record<OrderCallType, string> = {
  [HelpTypeSite.OrderCall]: 'FORM_ORDER_CALL_SUBMIT',
  [HelpTypeSite.BecomePartner]: 'FORM_BECOME_PARTNER_SUBMIT',
  [HelpTypeSite.FindOutTerms]: 'FORM_FIND_OUT_TERMS_SUBMIT',
}

interface IFormData {
  name: string
  phone: string
  accept: boolean
}

const schema = yupObject().shape({
  name: requiredFieldValidator,
  phone: phoneValidator,
  accept: acceptTermsValidator,
})

type OrderCallProps = {
  helpType: OrderCallType
  title?: string
}
export const OrderCall: FunctionComponent<OrderCallProps> = observer(
  ({ helpType, title }) => {
    const {
      register,
      handleSubmit,
      setValue,
      formState: { isSubmitting, errors },
    } = useForm<IFormData>({
      mode: 'onChange',
      resolver: yupResolver(schema),
    })

    const { getToken } = useCaptcha()

    const submit: SubmitHandler<IFormData> = useCallback(
      async (data) => {
        const tokenCaptcha = await getToken()
        const response = await getHelp(
          {
            type: helpType,
            data: {
              sourceUrl: window.location.href.split('?')[0],
              phone: data.phone,
              name: data.name,
              utmClientId: marketingStorage.getClientId() ?? undefined,
            },
          },
          tokenCaptcha
        )

        if (response.status) {
          marketingStorage.clearClientId()
          if (YANDEX_METRIKA) {
            // @ts-ignore
            ym(YANDEX_METRIKA_COUNTER, 'reachGoal', MetricGoal[helpType])
          }
          setValue('name', '')
          setValue('phone', '')
          orderCallPopupStore.hide()
          maxPopupStore.show({
            sourceSite: response.data.params,
            ...maxRequestFormTexts,
          })
        } else {
          toast.error('Что то пошло не так! Попробуйте позже')
        }
      },
      [setValue, getToken, helpType]
    )

    return (
      <Popup onClose={orderCallPopupStore.hide} className={styles.popup}>
        <form className={styles.container} onSubmit={handleSubmit(submit)}>
          <div className={styles.title}>{title ?? 'Заказать звонок'}</div>
          <FormInput
            className={styles.input}
            placeholder="Имя"
            error={errors.name?.message}
            {...register('name')}
          />
          <FormInput
            className={styles.input}
            mask="+{7} (000) 000-00-00"
            placeholder="+7 (999) 999-99-99"
            error={errors.phone?.message}
            {...register('phone')}
          />
          <Button
            type="submit"
            className={styles.button}
            disabled={isSubmitting}
            compactHover
          >
            Отправить
            {isSubmitting ? <Loader onTop /> : null}
          </Button>
          <AcceptCheckbox<IFormData>
            className={styles.checkbox}
            register={register}
            error={errors.accept?.message}
          />
        </form>
      </Popup>
    )
  }
)
